import React, { useState, useEffect, useRef } from "react";
import {
  FaUniversity,
  FaPlus,
  FaCheck,
  FaSpinner,
  FaExclamationTriangle,
  FaHistory,
  FaCamera,
  FaImage,
  FaEdit,
  FaTrash,
  FaTimes,
} from "react-icons/fa";
import { useNotification } from "@/contexts/NotificationContext";
import { bankBalanceService } from "@/services/bankBalance.service";
import { newFinanceService } from "@/services/newFinance.service";
import type { BankBalance, BankBalanceFormData } from "@/types/bankBalance";

// 은행 잔고 입력 및 대조 컴포넌트
export const BankBalanceManager: React.FC = () => {
  const { addNotification } = useNotification();
  const [balances, setBalances] = useState<BankBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [formData, setFormData] = useState<BankBalanceFormData>({
    date: new Date().toISOString().split("T")[0],
    bankBalance: 0,
    accountName: "OTP Bank",
    differenceExplanation: "",
    notes: "",
    screenshotFile: undefined,
  });

  const [calculatedBalance, setCalculatedBalance] = useState(0);

  // 초기 이월금 상수 (TODO: 이월금 서비스 연동 시 교체)
  const INITIAL_CARRYOVER = 0;

  useEffect(() => {
    loadBalances();
  }, []);

  // 장부상 잔고 계산 로직
  useEffect(() => {
    const calculateSystemBalance = async () => {
      if (!formData.date) return;

      try {
        // 모든 승인된 재정 기록 가져오기
        const allFinanceRecords =
          await newFinanceService.getAllFinanceRecords();

        // 승인된 기록만 필터링
        const approvedRecords = allFinanceRecords.filter(
          (record) =>
            record.status === "approved" && record.date <= formData.date,
        );

        // netTotals.HUF 합계 계산
        const totalNetHUF = approvedRecords.reduce(
          (sum, record) => sum + record.netTotals.HUF,
          0,
        );

        // 최종 장부상 잔고 계산 (이월금 + 순이익 합계)
        const systemBalance = INITIAL_CARRYOVER + totalNetHUF;

        setCalculatedBalance(systemBalance);
      } catch (error) {
        console.error("장부상 잔고 계산 오류:", error);
        setCalculatedBalance(INITIAL_CARRYOVER);
      }
    };

    calculateSystemBalance();
  }, [formData.date]);

  const loadBalances = async () => {
    setLoading(true);
    try {
      const data = await bankBalanceService.getRecentBankBalances(12);
      setBalances(data);
    } catch (error) {
      console.error("은행 잔고 로딩 오류:", error);
      addNotification("은행 잔고를 불러올 수 없습니다.", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split("T")[0],
      bankBalance: 0,
      accountName: "OTP Bank",
      differenceExplanation: "",
      notes: "",
      screenshotFile: undefined,
    });
    setPreviewUrl(null);
    setEditingId(null);
    setShowAddForm(false);
  };

  const handleAdd = async () => {
    // 기본 유효성 검사
    if (formData.bankBalance <= 0) {
      addNotification("은행 잔고를 입력해주세요.", "error");
      return;
    }

    // 차액 검사
    const difference = formData.bankBalance - calculatedBalance;
    if (difference !== 0 && !(formData.differenceExplanation || "").trim()) {
      addNotification(
        "차액이 발생했습니다. 차액 사유를 필수 입력해주세요.",
        "error",
      );
      return;
    }

    setActionLoading(true);
    try {
      await bankBalanceService.createBankBalance(formData, calculatedBalance);
      addNotification("은행 잔고가 기록되었습니다.", "success");
      await loadBalances();
      resetForm();
    } catch (error: any) {
      console.error("은행 잔고 저장 오류:", error);
      addNotification(error.message || "저장 중 오류가 발생했습니다.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, screenshotFile: file }));
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setFormData((prev) => ({ ...prev, screenshotFile: file }));
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleEdit = (balance: BankBalance) => {
    setEditingId(balance.id);
    setFormData({
      date: balance.date,
      bankBalance: balance.bankBalance,
      accountName: balance.accountName,
      differenceExplanation: balance.differenceExplanation || "",
      notes: balance.notes || "",
      screenshotFile: undefined,
    });
    if (balance.screenshotUrl) {
      setPreviewUrl(balance.screenshotUrl);
    }
    setShowAddForm(true);
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    setActionLoading(true);
    try {
      await bankBalanceService.updateBankBalance(editingId, formData);
      addNotification("은행 잔고가 수정되었습니다.", "success");
      await loadBalances();
      resetForm();
    } catch (error: any) {
      addNotification(error.message || "수정 중 오류가 발생했습니다.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id: string, date: string) => {
    if (!confirm(`${date} 은행 잔고 기록을 삭제하시겠습니까?`)) return;
    setActionLoading(true);
    try {
      await bankBalanceService.deleteBankBalance(id);
      addNotification("삭제되었습니다.", "success");
      await loadBalances();
    } catch (error: any) {
      addNotification(error.message || "삭제 중 오류가 발생했습니다.", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerify = async (id: string) => {
    try {
      await bankBalanceService.verifyBankBalance(id);
      addNotification("검증 완료", "success");
      await loadBalances();
    } catch (error: any) {
      addNotification(error.message || "검증 오류", "error");
    }
  };

  const formatCurrency = (amount: number): string =>
    `${amount.toLocaleString()} Ft`;
  const difference = formData.bankBalance - calculatedBalance;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden h-full flex flex-col">
      {/* 헤더 */}
      <div className="px-6 py-5 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <FaUniversity className="h-8 w-8 text-white mr-3" />
            <div>
              <h3 className="text-xl font-bold text-white">은행 잔고 관리</h3>
              <p className="text-blue-100 text-sm">
                HUF 계좌 잔고를 기록하고 대조합니다
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
          >
            <FaPlus className="h-4 w-4 mr-2" />
            잔고 기록
          </button>
        </div>
      </div>

      {/* 입력 폼 */}
      {showAddForm && (
        <div className="px-6 py-5 bg-blue-50 dark:bg-blue-900/20 border-b">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
              {editingId ? "✏️ 기록 수정" : "📅 새 기록"}
            </h4>
            <button
              onClick={resetForm}
              className="text-gray-500 hover:text-gray-700"
            >
              <FaTimes className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                기록일
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, date: e.target.value }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                계좌명
              </label>
              <input
                type="text"
                value={formData.accountName}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    accountName: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                잔고 (HUF)
              </label>
              <input
                type="number"
                value={formData.bankBalance}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    bankBalance: parseFloat(e.target.value) || 0,
                  }))
                }
                className="w-full px-3 py-2 border rounded-lg text-right font-mono dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          {/* 차액 표시 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 mb-4 border">
            <div className="flex justify-between text-sm">
              <span>
                장부상 잔고 (이월금+수입-지출):{" "}
                {formatCurrency(calculatedBalance)}
              </span>
              <span
                className={difference === 0 ? "text-green-600" : "text-red-600"}
              >
                차액: {difference >= 0 ? "+" : ""}
                {formatCurrency(difference)}
              </span>
            </div>
          </div>

          {difference !== 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FaExclamationTriangle className="inline mr-1 text-yellow-500" />
                차액 설명 (필수)
              </label>
              <input
                type="text"
                value={formData.differenceExplanation}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    differenceExplanation: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-yellow-300 rounded-lg"
                placeholder="예: 이자 수입 / 수수료"
              />
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              메모
            </label>
            <input
              type="text"
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>

          {/* 스크린샷 업로드 (드래그앤드롭) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FaCamera className="inline mr-1" /> 스크린샷
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`cursor-pointer p-6 border-2 border-dashed rounded-lg text-center transition-all ${
                isDragging
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 hover:border-blue-400"
              }`}
            >
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <FaImage className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                클릭 또는 드래그하여 업로드
              </p>
            </div>
            {previewUrl && (
              <div className="mt-3 relative inline-block">
                <img
                  src={previewUrl}
                  alt="미리보기"
                  className="h-20 rounded border"
                />
                <button
                  onClick={() => {
                    setPreviewUrl(null);
                    setFormData((p) => ({ ...p, screenshotFile: undefined }));
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 text-sm"
                >
                  ×
                </button>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={resetForm}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              취소
            </button>
            <button
              onClick={editingId ? handleUpdate : handleAdd}
              disabled={actionLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:bg-gray-400"
            >
              {actionLoading ? (
                <FaSpinner className="inline animate-spin mr-2" />
              ) : null}
              {editingId ? "수정" : "저장"}
            </button>
          </div>
        </div>
      )}

      {/* 목록 */}
      <div className="p-6 flex-1 overflow-y-auto">
        {loading ? (
          <div className="text-center py-12">
            <FaSpinner className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
          </div>
        ) : balances.length === 0 ? (
          <div className="text-center py-12">
            <FaHistory className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">기록된 잔고가 없습니다.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {balances.map((balance) => (
              <div
                key={balance.id}
                className={`border rounded-lg p-4 ${balance.isVerified ? "bg-green-50 border-green-200" : "bg-white border-gray-200"}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{balance.date}</span>
                      <span className="text-sm text-gray-500">
                        {balance.accountName}
                      </span>
                      {balance.isVerified && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                          <FaCheck className="inline mr-1" />
                          검증됨
                        </span>
                      )}
                      {balance.screenshotUrl && (
                        <a
                          href={balance.screenshotUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full"
                        >
                          <FaImage className="inline mr-1" />
                          스크린샷
                        </a>
                      )}
                    </div>
                    <div className="text-2xl font-bold text-blue-600 mt-1">
                      {formatCurrency(balance.bankBalance)}
                    </div>
                    {balance.difference !== 0 && (
                      <div className="text-sm text-red-600">
                        차액: {formatCurrency(balance.difference)}{" "}
                        {balance.differenceExplanation &&
                          `(${balance.differenceExplanation})`}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!balance.isVerified && (
                      <button
                        onClick={() => handleVerify(balance.id)}
                        className="p-2 text-green-600 hover:bg-green-100 rounded"
                        title="검증"
                      >
                        <FaCheck />
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(balance)}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                      title="수정"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDelete(balance.id, balance.date)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded"
                      title="삭제"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
